import BrandName from "./BrandName";
import React, { useEffect, useState } from "react";
import "./DiscoveryPage.css";
import MenuImage, { imageUrl } from "./MenuImage";

const PERIODS = [
  { id: "week", name: "Last Week" },
  { id: "month", name: "Last Month" },
  { id: "allTime", name: "All-time" },
];

const pct = (value) =>
  new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(
    value * 100,
  );

function TrendIndicator({ item }) {
  if (item.trend?.consecutiveRises)
    return (
      <span className="discovery-trend discovery-trend-up">
        ↑ Rising for 3 periods
      </span>
    );
  const points = item.movementPts;
  if (points === null || points === undefined) return null;
  if (Math.abs(points) < 0.05)
    return <span className="discovery-trend">Holding steady</span>;
  return (
    <span
      className={`discovery-trend ${
        points < 0 ? "discovery-trend-down" : "discovery-trend-up"
      }`}
    >
      {points > 0 ? "↑ Trending up" : "↓ Trending down"}{" "}
      {Math.abs(points).toFixed(1)} pts
    </span>
  );
}

function RisingNow({ views }) {
  const candidates = views
    .flatMap((view) =>
      view.groups.flatMap((group) =>
        group.items.map((item) => ({ item, group })),
      ),
    )
    .filter(
      ({ item }) =>
        item.rank > 3 &&
        (item.trend?.consecutiveRises || Number(item.movementPts) > 0),
    )
    .sort((a, b) => {
      const aStreak = a.item.trend?.consecutiveRises ? 1 : 0;
      const bStreak = b.item.trend?.consecutiveRises ? 1 : 0;
      return (
        bStreak - aStreak ||
        Number(b.item.movementPts || 0) - Number(a.item.movementPts || 0) ||
        b.item.share - a.item.share
      );
    })
    .filter(
      ({ item }, index, entries) =>
        entries.findIndex((entry) => entry.item.itemId === item.itemId) ===
        index,
    )
    .slice(0, 3);

  const entries = candidates.map(({ item, group }) => ({
    item,
    group,
    kind: item.trend?.consecutiveRises
      ? item.trend.neverTopThree
        ? "Underdog · 3-period rise"
        : "Rising star · 3-period rise"
      : `Trending up · ${Number(item.movementPts).toFixed(1)} pts`,
  }));

  if (!entries.length) return null;
  return (
    <aside className="discovery-rising-now" aria-label="Rising now">
      <div className="discovery-rising-now-heading">
        <p className="discovery-eyebrow">BEYOND THE TOP THREE</p>
        <h2>Rising now</h2>
        <p>Items gaining ground within their original menu category.</p>
      </div>
      <div className="discovery-rising-now-items">
        {entries.map(({ item, group, kind }) => (
          <article key={item.itemId}>
            <MenuImage image={item.image} alt={item.name} />
            <span
              className={`discovery-rising-label ${
                kind.startsWith("Underdog") ? "is-underdog" : ""
              }`}
            >
              {kind}
            </span>
            <h3>{item.name}</h3>
            <p>
              <strong>{pct(item.share)}%</strong> of{" "}
              {group.categoryName.toLowerCase()} orders
            </p>
            <span className="discovery-rising-rank">
              #{item.rank} in {group.categoryName}
            </span>
          </article>
        ))}
      </div>
    </aside>
  );
}

// Rotate on Mondays in the venue timezone, consistently across visitors.
export function selectDiscoverySpotlight(views, now = new Date()) {
  const entries = views.flatMap((view) =>
    view.groups.flatMap((group) =>
      group.items.map((item, index) => ({ item, group, index })),
    ),
  );
  const featuredIds = new Set(
    entries
      .filter(({ item, index }) => item.rank <= 3 || index < 3)
      .map(({ item }) => item.itemId),
  );
  const candidates = entries
    .filter(
      ({ item }) =>
        item.rank > 3 &&
        !featuredIds.has(item.itemId) &&
        imageUrl(item.image?.baseUrl, 640),
    )
    .filter(
      ({ item }, index, rows) =>
        rows.findIndex((row) => row.item.itemId === item.itemId) === index,
    )
    .sort((a, b) => a.item.itemId.localeCompare(b.item.itemId));
  if (!candidates.length) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: candidates[0].group.timezone || "Europe/London",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(now);
  const part = (name) =>
    Number(parts.find((entry) => entry.type === name).value);
  const day = Date.UTC(part("year"), part("month") - 1, part("day"));
  const week = Math.floor((day - Date.UTC(1970, 0, 5)) / (7 * 86400000));
  return candidates[
    ((week % candidates.length) + candidates.length) % candidates.length
  ];
}

function DiscoverySpotlight({ views }) {
  const spotlight = selectDiscoverySpotlight(views);
  if (!spotlight) return null;
  const { item, group } = spotlight;
  return (
    <aside
      className="discovery-spotlight"
      aria-labelledby="discovery-spotlight-title"
    >
      <div className="discovery-spotlight-copy">
        <p className="discovery-eyebrow">A LITTLE EXPLORATION</p>
        <h2 id="discovery-spotlight-title">
          Beyond
          <br />
          <em>the usual.</em>
        </h2>
        <p className="discovery-spotlight-intro">
          There’s more to the menu than the most ordered. Make room for
          something different.
        </p>
        <p className="discovery-spotlight-category">{group.categoryName}</p>
        <h3>{item.name}</h3>
        {item.description && (
          <p className="discovery-spotlight-description">{item.description}</p>
        )}
        <a className="discovery-explore" href="/#menu-heading">
          Explore the menu <span aria-hidden="true">↗</span>
        </a>
        <p className="discovery-spotlight-note">
          A weekly rotating spotlight from outside the top three in this
          selection. Chosen for exploration, not as a popularity award.
        </p>
      </div>
      <MenuImage
        key={item.itemId}
        image={item.image}
        alt={item.name}
        sizes="(max-width: 700px) 100vw, 55vw"
      />
    </aside>
  );
}

function DishCard({ item, group }) {
  return (
    <article
      className={`discovery-dish ${imageUrl(item.image?.baseUrl, 640) ? "has-photo" : "text-only"}`}
    >
      <MenuImage image={item.image} alt={item.name} />
      <div className="discovery-dish-body">
        <span className="discovery-rank">
          {item.tied ? "Joint " : ""}#{item.rank} in {group.categoryName}
        </span>
        <h3>{item.name}</h3>
        <p className="discovery-share">
          <strong>
            {pct(item.share)}
            <span>%</span>
          </strong>
          <span>of {group.categoryName.toLowerCase()} orders</span>
        </p>

        <TrendIndicator item={item} />
        {item.modifierInsights?.map((insight) => (
          <p className="discovery-option" key={insight.groupId}>
            <span>Most popular {insight.groupName.toLowerCase()}</span>
            <strong>{insight.optionName}</strong>
            <span>{pct(insight.share)}% chose this option</span>
          </p>
        ))}
      </div>
    </article>
  );
}

async function getDiscovery(query, signal) {
  const response = await fetch(`/api/discovery?${query}`, {
    signal,
    credentials: "omit",
    cache: "no-store",
  });
  let body = null;
  try {
    body = await response.json();
  } catch {
    // The local proxy and upstream may return plain text while unavailable.
  }
  if (!response.ok || !body?.success) {
    throw new Error(
      response.status === 404
        ? "Our favourites are getting ready. Please browse the menu for now."
        : "We couldn’t refresh the favourites just now.",
    );
  }
  if (
    !body.data ||
    !Array.isArray(body.data.views) ||
    body.data.views.some((view) =>
      !view || typeof view.id !== "string" || typeof view.name !== "string" ||
      !Array.isArray(view.groups) || view.groups.some((group) =>
        !group || !Array.isArray(group.items) || !Array.isArray(group.subcategories)
      )
    )
  ) {
    throw new Error("We couldn’t read the latest favourites.");
  }
  return body.data;
}

export default function DiscoveryPage() {
  const [period, setPeriod] = useState("week");
  const [refresh, setRefresh] = useState(0);
  const [state, setState] = useState({ loading: true, views: [], error: "" });

  useEffect(() => {
    document.title = "What’s everyone ordering? | nomii";
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    async function loadDashboard() {
      setState((current) => ({ ...current, loading: true, error: "" }));
      try {
        const { views } = await getDiscovery(
          new URLSearchParams({ period, view: "dashboard" }),
          controller.signal,
        );
        if (active) setState({ loading: false, views, error: "" });
      } catch (error) {
        if (active && error.name !== "AbortError") {
          setState({
            loading: false,
            views: [],
            error: error.message || "We couldn’t load the favourites.",
          });
        }
      }
    }

    loadDashboard();
    return () => {
      active = false;
      controller.abort();
    };
  }, [period, refresh]);

  // A photographed category leader, never a cross-category popularity ranking.
  const hero =
    !state.loading && !state.error
      ? state.views
          .flatMap((view) =>
            view.groups.map((group, groupIndex) => ({
              group,
              item: group.items.find(
                (item) => item.rank === 1 && imageUrl(item.image?.baseUrl, 640),
              ),
              viewId: view.id,
            })),
          )
          .find((entry) => entry.item)
      : null;

  return (
    <div className="nomi-discovery">
      <a className="discovery-skip" href="#discover-results">
        Skip to popular items
      </a>
      <header className="discovery-header">
        <a className="discovery-wordmark" href="/" aria-label="nomii home">
          <img src="/elephant-logo.png" alt="nomii" />
        </a>
        <a className="discovery-menu-link" href="/#menu-heading">
          Our menu <span aria-hidden="true">↗</span>
        </a>
      </header>
      <main>
        <section className={`discovery-intro ${hero ? "has-hero" : ""}`}>
          <div className="discovery-intro-text">
            <p className="discovery-eyebrow discovery-brand-intro">A taste of <BrandName /></p>
            <h1>
              Find your
              <br />
              <em>next favourite.</em>
            </h1>
            <p className="discovery-intro-copy">
              See which items are ordered most often by our dine-in guests.
            </p>
            <a className="discovery-explore" href="#discover-results">
              Follow your appetite <span aria-hidden="true">↓</span>
            </a>
          </div>
          {hero && (
            <figure className="discovery-hero">
              <MenuImage
                image={hero.item.image}
                alt={hero.item.name}
                priority
                sizes="(max-width: 700px) 100vw, 55vw"
              />
              <figcaption>
                <span className="discovery-eyebrow">
                  {PERIODS.find((choice) => choice.id === period).name} ·{" "}
                  {hero.item.tied ? "Joint " : ""}#1 in{" "}
                  {hero.group.categoryName}
                </span>
                <a href={`#discover-${hero.viewId}`}>
                  {hero.item.name} <span aria-hidden="true">↗</span>
                </a>
              </figcaption>
            </figure>
          )}
        </section>

        <section className="discovery-browse" aria-label="Explore favourites">
          {state.views.length > 0 && (
            <nav className="discovery-category-controls" aria-label="Jump to">
              {state.views.map((view) => (
                <a key={view.id} href={`#discover-${view.id}`}>
                  {view.name}
                </a>
              ))}
            </nav>
          )}
          <div className="discovery-toolbar">
            <div
              className="discovery-period-controls"
              role="group"
              aria-label="Order period"
            >
              {PERIODS.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  aria-pressed={period === choice.id}
                  onClick={() => setPeriod(choice.id)}
                >
                  {choice.name}
                </button>
              ))}
            </div>
            <p className="discovery-scope">Dine-in orders only</p>
          </div>
        </section>

        <section
          id="discover-results"
          className="discovery-results"
          aria-busy={state.loading}
        >
          {state.error && (
            <div className="discovery-notice" role="alert">
              <p>{state.error}</p>
              <button type="button" onClick={() => setRefresh((n) => n + 1)}>
                Try again
              </button>
            </div>
          )}
          {state.loading && (
            <div className="discovery-empty" role="status">
              Gathering the favourites…
            </div>
          )}
          {!state.loading &&
            state.views.map((view, viewIndex) => (
              <React.Fragment key={view.id}>
                <section
                  className={`discovery-dashboard-view discovery-chapter-${viewIndex % 3}`}
                  id={`discover-${view.id}`}
                  key={view.id}
                >
                  <div className="discovery-view-heading">
                    <p className="discovery-eyebrow">
                      {PERIODS.find((choice) => choice.id === period).name}
                    </p>
                    <h2>Favourite {view.name.toLowerCase()}</h2>
                    <span
                      className="discovery-chapter-number"
                      aria-hidden="true"
                    >
                      {String(viewIndex + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="discovery-category-groups">
                    {view.groups.map((group, groupIndex) => (
                      <section
                        className={`discovery-category-group discovery-layout-${groupIndex % 2}`}
                        key={`${view.id}-${group.subcategory || "all"}`}
                      >
                        {(view.groups.length > 1 ||
                          group.categoryName.toLowerCase() !==
                            view.name.toLowerCase()) && (
                          <div className="discovery-group-heading">
                            <h3>{group.categoryName}</h3>
                          </div>
                        )}
                        {group.items.length ? (
                          <>
                            <div className="discovery-dish-grid">
                              {group.items.slice(0, 3).map((item) => (
                                <DishCard
                                  key={item.itemId}
                                  item={item}
                                  group={group}
                                />
                              ))}
                            </div>
                          </>
                        ) : (
                          <p className="discovery-group-empty">
                            Not enough ordering data to show favourites here
                            yet.
                          </p>
                        )}
                      </section>
                    ))}
                  </div>
                </section>
                {viewIndex === 0 && <RisingNow views={state.views} />}
                {viewIndex === Math.min(1, state.views.length - 1) && (
                  <DiscoverySpotlight views={state.views} />
                )}
              </React.Fragment>
            ))}
        </section>

        <aside className="discovery-method">
          <span aria-hidden="true">✳</span>
          <div>
            <h2>Favourites &amp; fresh discoveries.</h2>
            <p>
              Rankings reflect recorded dine-in orders. Beyond the usual rotates
              weekly to give other dishes a moment in the spotlight; it isn’t a
              sales ranking.
            </p>
          </div>
          <a href="/#menu-heading">Find your next favourite on the menu ↗</a>
        </aside>
      </main>
      <footer className="discovery-footer">
        <a href="/">Back to nomii</a>
      </footer>
    </div>
  );
}
